// General
import React, { useState, useContext, useEffect } from "react";
// Styles
import { Modal } from "react-bootstrap";
import { TextField, Autocomplete } from "@mui/material";
// Formik
import { Formik, Form } from "formik";
// Contexts
import { CustomerContext } from "../../data/customer/CustomerContext";
import { useValidations } from "../../data/ValidationContext";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { getCities, getDistricts } from "../../data/general/LocationSlice";
// Components
import CapitalizedFormikField from "../../components/CapitalizedFormikField"

//Function
function CustomerUpdateModal({ show, handleClose, customer }) {
  const { updateCustomer } = useContext(CustomerContext);
  const dispatch = useDispatch();
  const { cities, districts } = useSelector((state) => state.location);
  const { vs_customer } = useValidations();

  const [searchedCity, setSearchedCity] = useState(customer?.city || "");

  // müşteri değiştiğinde tür ve şehir güncelle
  useEffect(() => {
    if (customer) setSearchedCity(customer.city || "");
  }, [customer]);

  // şehirleri yükle
  useEffect(() => {
    if (show && cities.length === 0) dispatch(getCities({ signal: null }));
  }, [show, cities.length, dispatch]);

  // seçilen şehre göre ilçeleri yükle
  useEffect(() => {
    if (searchedCity) dispatch(getDistricts({ signal: null, city: searchedCity }));
  }, [searchedCity, dispatch]);

  if (!customer) return null;

  const initialValues = {
    fullName: customer.fullName || "",
    phone1: customer.phone1 || "",
    phone2: customer.phone2 || "",
    customerType: customer.customerType || "Bireysel",
    tcNo: customer.tcNo || "",
    vergiNo: customer.vergiNo || "",
    vergiDairesi: customer.vergiDairesi || "",
    city: customer.city || "",
    district: customer.district || "",
    address: customer.address || "",
  };

  const fieldWrapperStyle = { marginBottom: "0.5rem" };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered style={{ zIndex: 9000 }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={vs_customer}
        onSubmit={(values) => {
          updateCustomer(customer.id, values);
          handleClose();
        }}
      >
        {({ values, setFieldValue, setFieldTouched, touched, errors }) => (
          <Form>
            <Modal.Header closeButton>
              <Modal.Title>Müşteri Güncelle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {/* Ad Soyad ve Müşteri Türü */}
              <div className="row mb-3">
                <div className="col" style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="fullName"
                    label="Ad Soyad"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </div>
                <div className="col" style={fieldWrapperStyle}>
                  <select
                    name="customerType"
                    className="form-select"
                    value={values.customerType}
                    onChange={(e) => setFieldValue("customerType", e.target.value)}
                  >
                    <option value="Bireysel">Bireysel</option>
                    <option value="Kurumsal">Kurumsal</option>
                  </select>
                </div>
              </div>

              {/* Bireysel / Kurumsal Alanlar */}
              {values.customerType === "Bireysel" ? (
                <div style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="tcNo"
                    label="T.C. Kimlik No"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </div>
              ) : (
                <div className="row mb-3">
                  <div className="col" style={fieldWrapperStyle}>
                    <CapitalizedFormikField
                      name="vergiNo"
                      label="Vergi No"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </div>
                  <div className="col" style={fieldWrapperStyle}>
                    <CapitalizedFormikField
                      name="vergiDairesi"
                      label="Vergi Dairesi"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </div>
                </div>
              )}

              {/* Telefonlar */}
              <div className="row mb-3">
                <div className="col" style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="phone1"
                    label="Telefon 1"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </div>
                <div className="col" style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="phone2"
                    label="Telefon 2"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </div>
              </div>

              {/* Şehir ve İlçe Autocomplete */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <Autocomplete
                    options={cities || []}
                    value={values.city || null}
                    onChange={(e, newValue) => {
                      setFieldValue("city", newValue || "");
                      setFieldValue("district", "");
                      if (newValue && newValue !== searchedCity) setSearchedCity(newValue);
                    }}
                    onBlur={() => setFieldTouched("city", true)}
                    getOptionLabel={(option) => option?.name || option || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Şehir"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={touched.city && !!errors.city}
                        helperText={touched.city && errors.city}
                      />
                    )}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "zIndex",
                            enabled: true,
                            phase: "write",
                            fn: ({ state }) => {
                              state.styles.popper.zIndex = 9000;
                            },
                          },
                        ],
                      },
                    }}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <Autocomplete
                    options={districts || []}
                    value={values.district || null}
                    onChange={(e, newValue) => setFieldValue("district", newValue || "")}
                    onBlur={() => setFieldTouched("district", true)}
                    getOptionLabel={(option) => option?.name || option || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="İlçe"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={touched.district && !!errors.district}
                        helperText={touched.district && errors.district}
                      />
                    )}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "zIndex",
                            enabled: true,
                            phase: "write",
                            fn: ({ state }) => {
                              state.styles.popper.zIndex = 9999;
                            },
                          },
                        ],
                      },
                    }}
                  />
                </div>
              </div>

              {/* Adres */}
              <div style={fieldWrapperStyle}>
                <CapitalizedFormikField
                  name="address"
                  label="Adres"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary">
                Güncelle
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default CustomerUpdateModal;
